import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';

const NotFound: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission, isAdmin } = usePermissions();

  const getHomeRoute = (): string => {
    if (!user) return '/login';
    if (isAdmin || hasPermission(PERMISSIONS.SYSTEM_ADMIN)) return '/management';
    return '/hub/dashboard';
  };

  const showClientsLink = hasPermission(PERMISSIONS.PARTNERS_VIEW) || hasPermission(PERMISSIONS.PARTNERS_MANAGE);
  const showIntegrationsLink = hasPermission(PERMISSIONS.INTEGRATIONS_VIEW) || hasPermission(PERMISSIONS.INTEGRATIONS_MANAGE);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8 transition-colors">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h1 className="text-9xl font-bold text-gray-300 dark:text-gray-600 mb-4">
            404
          </h1>

          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Page not found
          </h2>

          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
            Sorry, we couldn&apos;t find the page you&apos;re looking for.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-3 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
            >
              ← Go Back
            </button>

            <Link
              to={getHomeRoute()}
              className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors text-center"
            >
              Go to {user ? 'Home' : 'Login'}
            </Link>
          </div>

          {user && (
            <div className="mt-8 text-sm">
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                Or try one of these:
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/hub/dashboard"
                  className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                >
                  Dashboard
                </Link>
                <Link
                  to="/hub/projects"
                  className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                >
                  Projects
                </Link>
                {showClientsLink && (
                  <Link
                    to="/hub/clients"
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                  >
                    Clients
                  </Link>
                )}
                {showIntegrationsLink && (
                  <Link
                    to="/integrations"
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                  >
                    Integrations
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotFound;
